import { useCallback, useEffect, useState } from "react";

const SHARED_KEY = "translationWindowSettings";
const DEFAULT_HEADER = {
  enabled: false, text: "Digite Aqui Sua Mensagem", fontSize: 24,
  textColor: "#000000", bgColor: "#FFFFFF", borderRadius: 8, padding: 16,
};
const DEFAULT_FOOTER = {
  enabled: false,
  text: "Esta tradução é feita de forma automática. Pedimos desculpas por possíveis erros que possam acontecer. / This translation is done automatically. We apologize for any errors that may occur.",
  fontSize: 16, textColor: "#000000", bgColor: "#FFFFFF", borderRadius: 8, padding: 12,
};

const readSettings = (key) => {
  try {
    const value = JSON.parse(localStorage.getItem(key) || "{}");
    return value && typeof value === "object" && !Array.isArray(value) ? value : {};
  } catch {
    return {};
  }
};

// Both windows use the Dashboard's messages; overlay remains independent per window.
export const useTranslationWindowSettings = (overlayKey = SHARED_KEY, presenterSettings = null) => {
  const read = useCallback(() => {
    const shared = readSettings(SHARED_KEY);
    const messages = presenterSettings || shared;
    const own = overlayKey === SHARED_KEY ? shared : readSettings(overlayKey);
    return {
      headerSettings: { ...DEFAULT_HEADER, ...messages.header },
      footerSettings: { ...DEFAULT_FOOTER, ...messages.footer },
      overlayMode: own.overlayMode ?? false,
    };
  }, [overlayKey, presenterSettings]);
  const [settings, setSettings] = useState(read);

  // Dashboard overlay changes apply to the primary window. Local overlay
  // controls remain independent and are not reset by repeated snapshots.
  const presenterOverlay = presenterSettings?.overlayMode;
  useEffect(() => {
    if (overlayKey !== SHARED_KEY || typeof presenterOverlay !== "boolean") return;
    const own = readSettings(overlayKey);
    own.overlayMode = presenterOverlay;
    localStorage.setItem(overlayKey, JSON.stringify(own));
    setSettings(prev => ({ ...prev, overlayMode: presenterOverlay }));
  }, [overlayKey, presenterOverlay]);

  useEffect(() => {
    const sync = () => {
      const next = read();
      setSettings(prev => JSON.stringify(prev) === JSON.stringify(next) ? prev : next);
    };
    const onStorage = (event) => {
      if (event.key === null || event.key === SHARED_KEY || event.key === overlayKey) sync();
    };
    sync();
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", sync);
    const interval = setInterval(sync, 2000);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", sync);
      clearInterval(interval);
    };
  }, [read, overlayKey]);

  const toggleOverlayMode = useCallback(() => {
    const own = readSettings(overlayKey);
    own.overlayMode = !settings.overlayMode;
    localStorage.setItem(overlayKey, JSON.stringify(own));
    setSettings(prev => ({ ...prev, overlayMode: own.overlayMode }));
  }, [overlayKey, settings.overlayMode]);

  return { ...settings, toggleOverlayMode };
};